<template>
  <div id="printable" class="report-container">
    <!-- Header -->
    <div class="header">
      <div class="facility-name">
        TUURU COTTOLENGO HEALTH CENTRE - AR2026259
      </div>
      <div class="facility-details">
        <div>Igembe Central</div>
        <div>P.O BOX 77 - Meru | Tel: 254254746758069</div>
        <div>Email: suore.tuuru@cottolengo.org</div>
      </div>
      <div class="report-title">MOH 717 - SERVICE DELIVERY REPORT</div>
    </div>

    <!-- Month/Year and Generated By Bar -->
    <div class="info-bar">
      <div class="info-left">
        <div class="info-item">
          <span class="info-label">Month:</span>
          <select v-model="selectedMonth" class="month-select">
            <option value="January">January</option>
            <option value="February">February</option>
            <option value="March">March</option>
            <option value="April">April</option>
            <option value="May">May</option>
            <option value="June">June</option>
            <option value="July">July</option>
            <option value="August">August</option>
            <option value="September">September</option>
            <option value="October">October</option>
            <option value="November">November</option>
            <option value="December">December</option>
          </select>
        </div>
        <div class="info-item">
          <span class="info-label">Year:</span>
          <input
            type="number"
            v-model="selectedYear"
            class="year-input"
            min="2020"
            max="2030"
          />
        </div>
        <div class="info-item">
          <span class="info-label">Generated By:</span>
          <span class="info-value">{{ generatedBy }}</span>
        </div>
      </div>
      <button @click="printPage" class="btn-print">Print Report</button>
    </div>

    <!-- Period Display for Print -->
    <div class="print-period">
      <strong>Reporting Period:</strong> {{ selectedMonth }} {{ selectedYear }}
    </div>

    <!-- Section 1: Outpatient Services -->
    <div class="section">
      <div class="section-title">1. OUTPATIENT SERVICES</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">New Outpatient Visits</div>
          <div class="stat-value">{{ outpatient.newVisits }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Repeat Outpatient Visits</div>
          <div class="stat-value">{{ outpatient.repeatVisits }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Outpatient Visits</div>
          <div class="stat-value">{{ outpatient.totalVisits }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Daily Average</div>
          <div class="stat-value">{{ outpatient.dailyAverage }}</div>
        </div>
      </div>
    </div>

    <!-- Section 2: Inpatient Services -->
    <div class="section">
      <div class="section-title">2. INPATIENT SERVICES</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Total Admissions</div>
          <div class="stat-value">{{ inpatient.admissions }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Discharges</div>
          <div class="stat-value">{{ inpatient.discharges }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Bed Occupancy Rate</div>
          <div class="stat-value">{{ inpatient.bedOccupancyRate }}%</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Average Length of Stay</div>
          <div class="stat-value">{{ inpatient.avgLengthOfStay }} days</div>
        </div>
      </div>
    </div>

    <!-- Section 3: Maternity Services -->
    <div class="section">
      <div class="section-title">3. MATERNITY SERVICES</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Number</th>
              <th>Target</th>
              <th>Coverage (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="service in maternityServices" :key="service.name">
              <td>{{ service.name }}</td>
              <td class="text-center">{{ service.number }}</td>
              <td class="text-center">{{ service.target }}</td>
              <td class="text-center">{{ service.coverage }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 4: Child Health Services -->
    <div class="section">
      <div class="section-title">4. CHILD HEALTH SERVICES</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Service</th>
              <th>Number</th>
              <th>Target</th>
              <th>Coverage (%)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="service in childHealthServices" :key="service.name">
              <td>{{ service.name }}</td>
              <td class="text-center">{{ service.number }}</td>
              <td class="text-center">{{ service.target }}</td>
              <td class="text-center">{{ service.coverage }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 5: Family Planning Services -->
    <div class="section">
      <div class="section-title">5. FAMILY PLANNING SERVICES</div>
      <div class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Method</th>
              <th>New Acceptors</th>
              <th>Continuing Users</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fp in familyPlanning" :key="fp.method">
              <td>{{ fp.method }}</td>
              <td class="text-center">{{ fp.newAcceptors }}</td>
              <td class="text-center">{{ fp.continuingUsers }}</td>
              <td class="text-center">
                {{ fp.newAcceptors + fp.continuingUsers }}
              </td>
            </tr>
            <tr class="total-row">
              <td><strong>Total</strong></td>
              <td class="text-center">
                <strong>{{ totalFamilyPlanning.newAcceptors }}</strong>
              </td>
              <td class="text-center">
                <strong>{{ totalFamilyPlanning.continuingUsers }}</strong>
              </td>
              <td class="text-center">
                <strong>{{ totalFamilyPlanning.total }}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Section 6: Laboratory Services -->
    <div class="section">
      <div class="section-title">6. LABORATORY SERVICES</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Total Lab Tests</div>
          <div class="stat-value">{{ laboratory.totalTests }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Malaria RDT</div>
          <div class="stat-value">{{ laboratory.malariaRdt }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">HIV Tests</div>
          <div class="stat-value">{{ laboratory.hivTests }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Pregnancy Tests</div>
          <div class="stat-value">{{ laboratory.pregnancyTests }}</div>
        </div>
      </div>
    </div>

    <!-- Section 7: Pharmacy Services -->
    <div class="section">
      <div class="section-title">7. PHARMACY SERVICES</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Prescriptions Dispensed</div>
          <div class="stat-value">{{ pharmacy.prescriptions }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Essential Medicines Stockout Days</div>
          <div class="stat-value">{{ pharmacy.stockoutDays }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">ARV Patients Served</div>
          <div class="stat-value">{{ pharmacy.arvPatients }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Value (KSh)</div>
          <div class="stat-value">
            {{ pharmacy.totalValue.toLocaleString() }}
          </div>
        </div>
      </div>
    </div>

    <!-- Section 8: Referrals -->
    <div class="section">
      <div class="section-title">8. REFERRALS</div>
      <div class="stats-grid-2">
        <div class="stat-card">
          <div class="stat-label">Referrals In</div>
          <div class="stat-value">{{ referrals.referralsIn }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Referrals Out</div>
          <div class="stat-value">{{ referrals.referralsOut }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Referrals to Hospital</div>
          <div class="stat-value">{{ referrals.toHospital }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Referrals to Other Facilities</div>
          <div class="stat-value">{{ referrals.toOther }}</div>
        </div>
      </div>
    </div>

    <!-- Section 9: Community Health Services -->
    <div class="section">
      <div class="section-title">9. COMMUNITY HEALTH SERVICES</div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Active CHVs</div>
          <div class="stat-value">{{ community.activeCHVs }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Households Visited</div>
          <div class="stat-value">{{ community.householdsVisited }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Community Dialogues</div>
          <div class="stat-value">{{ community.communityDialogues }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Referrals from Community</div>
          <div class="stat-value">{{ community.communityReferrals }}</div>
        </div>
      </div>
    </div>

    <!-- Section 10: Health Promotion Activities -->
    <div class="section">
      <div class="section-title">10. HEALTH PROMOTION ACTIVITIES</div>
      <div class="stats-grid-3">
        <div class="stat-card">
          <div class="stat-label">Health Talks</div>
          <div class="stat-value">{{ healthPromotion.healthTalks }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Outreach Events</div>
          <div class="stat-value">{{ healthPromotion.outreachEvents }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Beneficiaries Reached</div>
          <div class="stat-value">
            {{ healthPromotion.beneficiariesReached }}
          </div>
        </div>
      </div>
    </div>

    <!-- Section 11: Challenges and Recommendations -->
    <div class="section">
      <div class="section-title">11. CHALLENGES AND RECOMMENDATIONS</div>
      <div class="feedback-box">
        <div class="feedback-item">
          <strong>Challenges Encountered:</strong>
          <p>{{ challenges.challenges }}</p>
        </div>
        <div class="feedback-item">
          <strong>Recommendations:</strong>
          <p>{{ challenges.recommendations }}</p>
        </div>
        <div class="feedback-item">
          <strong>Planned Activities for Next Month:</strong>
          <p>{{ challenges.plannedActivities }}</p>
        </div>
      </div>
    </div>

    <!-- Section 12: Sign Off -->
    <div class="section">
      <div class="section-title">12. REPORT SIGN OFF</div>
      <div class="signoff-grid">
        <div class="signoff-item">
          <span class="signoff-label">Report Compiled By:</span>
          <span class="signoff-value">{{ signOff.compiledBy }}</span>
        </div>
        <div class="signoff-item">
          <span class="signoff-label">Designation:</span>
          <span class="signoff-value">{{ signOff.designation }}</span>
        </div>
        <div class="signoff-item">
          <span class="signoff-label">Facility In-Charge:</span>
          <span class="signoff-value">{{ signOff.facilityIncharge }}</span>
        </div>
        <div class="signoff-item">
          <span class="signoff-label">Date:</span>
          <span class="signoff-value">{{ signOff.date }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MOH717ServiceDelivery",
  data() {
    return {
      selectedMonth: "April",
      selectedYear: 2026,
      generatedBy: "SR. JANE KAREGI MUNYUGI",

      outpatient: {
        newVisits: 1245,
        repeatVisits: 3420,
        totalVisits: 4665,
        dailyAverage: 156,
      },

      inpatient: {
        admissions: 278,
        discharges: 265,
        bedOccupancyRate: 82.5,
        avgLengthOfStay: 4.8,
      },

      maternityServices: [
        { name: "ANC 1st Visit", number: 187, target: 220, coverage: 85.0 },
        { name: "ANC 4+ Visits", number: 165, target: 190, coverage: 86.8 },
        { name: "Deliveries (SVD)", number: 138, target: 150, coverage: 92.0 },
        {
          name: "Deliveries (C-Section)",
          number: 16,
          target: 20,
          coverage: 80.0,
        },
        {
          name: "PNC within 48 hours",
          number: 145,
          target: 156,
          coverage: 92.9,
        },
        { name: "PNC Day 42", number: 125, target: 140, coverage: 89.3 },
      ],

      childHealthServices: [
        { name: "BCG Vaccination", number: 98, target: 105, coverage: 93.3 },
        { name: "OPV 3", number: 88, target: 100, coverage: 88.0 },
        { name: "DPT-HepB-Hib 3", number: 85, target: 100, coverage: 85.0 },
        { name: "Measles/Rubella 1", number: 82, target: 95, coverage: 86.3 },
        {
          name: "Vitamin A Supplementation",
          number: 95,
          target: 110,
          coverage: 86.4,
        },
        { name: "Growth Monitoring", number: 156, target: 180, coverage: 86.7 },
      ],

      familyPlanning: [
        { method: "Pills (COC/POP)", newAcceptors: 45, continuingUsers: 210 },
        { method: "Injectable (DMPA)", newAcceptors: 52, continuingUsers: 245 },
        { method: "Implants", newAcceptors: 28, continuingUsers: 98 },
        { method: "IUD", newAcceptors: 8, continuingUsers: 35 },
        { method: "Condoms", newAcceptors: 35, continuingUsers: 65 },
        { method: "Natural Methods", newAcceptors: 12, continuingUsers: 28 },
        {
          method: "Permanent (BTL/Vasectomy)",
          newAcceptors: 2,
          continuingUsers: 0,
        },
      ],

      laboratory: {
        totalTests: 1245,
        malariaRdt: 423,
        hivTests: 234,
        pregnancyTests: 156,
      },

      pharmacy: {
        prescriptions: 3420,
        stockoutDays: 3,
        arvPatients: 185,
        totalValue: 425000,
      },

      referrals: {
        referralsIn: 45,
        referralsOut: 52,
        toHospital: 38,
        toOther: 14,
      },

      community: {
        activeCHVs: 12,
        householdsVisited: 245,
        communityDialogues: 8,
        communityReferrals: 23,
      },

      healthPromotion: {
        healthTalks: 15,
        outreachEvents: 4,
        beneficiariesReached: 1250,
      },

      challenges: {
        challenges:
          "Limited laboratory supplies for sample collection. Staff shortage during weekends. Delayed supply chain for essential medicines.",
        recommendations:
          "Recruit additional staff. Improve supply chain management. Conduct regular stock monitoring.",
        plannedActivities:
          "Community outreach in Kianjai sub-location. Staff training on IDSR. Stocktaking and inventory management.",
      },

      signOff: {
        compiledBy: "SR. JANE KAREGI MUNYUGI",
        designation: "Health Records Officer",
        facilityIncharge: "DR. S. MWANGI",
        date: "2026-04-30",
      },
    };
  },
  computed: {
    totalFamilyPlanning() {
      let newAcceptors = 0;
      let continuingUsers = 0;
      this.familyPlanning.forEach((fp) => {
        newAcceptors += fp.newAcceptors;
        continuingUsers += fp.continuingUsers;
      });
      return {
        newAcceptors: newAcceptors,
        continuingUsers: continuingUsers,
        total: newAcceptors + continuingUsers,
      };
    },
  },
  methods: {
    printPage() {
      window.print();
    },
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.report-container {
  background: white;
  min-height: 100vh;
  padding: 20px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.header {
  text-align: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #1a56db;
}

.facility-name {
  font-size: 20px;
  font-weight: bold;
  color: #1a56db;
  margin-bottom: 6px;
}

.facility-details {
  font-size: 11px;
  color: #4b5563;
  line-height: 1.4;
  margin-bottom: 8px;
}

.report-title {
  font-size: 16px;
  font-weight: bold;
  background: #f3f4f6;
  display: inline-block;
  padding: 5px 16px;
  border-radius: 20px;
  color: #1f2937;
}

.info-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.info-left {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.info-value {
  font-size: 12px;
  color: #1f2937;
  font-weight: 500;
}

.month-select,
.year-input {
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
}

.btn-print {
  padding: 8px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}

.btn-print:hover {
  background: #2563eb;
}

.print-period {
  text-align: center;
  padding: 8px;
  margin-bottom: 20px;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  font-size: 12px;
  color: #92400e;
  border-radius: 6px;
}

.section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 14px;
  font-weight: bold;
  color: #1f2937;
  background: #e5e7eb;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 10px;
}

.stats-grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.stats-grid-3 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  text-align: center;
}

.stat-card .stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #059669;
}

.stat-card .stat-label {
  font-size: 11px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.data-table th {
  background: #1f2937;
  color: white;
  padding: 8px;
  text-align: center;
  border: 1px solid #374151;
}

.data-table td {
  border: 1px solid #d1d5db;
  padding: 8px;
  text-align: left;
}

.data-table td.text-center {
  text-align: center;
}

.total-row {
  background: #fef3c7;
  font-weight: bold;
}

.feedback-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
}

.feedback-item {
  margin-bottom: 12px;
}

.feedback-item strong {
  font-size: 12px;
  color: #374151;
  display: block;
  margin-bottom: 4px;
}

.feedback-item p {
  font-size: 11px;
  color: #4b5563;
  line-height: 1.4;
}

.signoff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.signoff-item {
  display: flex;
  flex-direction: column;
}

.signoff-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}

.signoff-value {
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
}

@media print {
  @page {
    size: portrait;
    margin: 0.5cm;
  }

  .btn-print,
  .month-select,
  .year-input {
    display: none;
  }

  .info-bar {
    display: none;
  }

  .print-period {
    background: #fef3c7;
    margin: 10px 0;
    padding: 5px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .report-container {
    padding: 0.2cm;
  }

  .header {
    margin-bottom: 5px;
  }

  .facility-name {
    font-size: 14px;
  }

  .facility-details {
    font-size: 8px;
  }

  .report-title {
    font-size: 11px;
  }

  .section-title {
    font-size: 11px;
    padding: 4px 8px;
  }

  .stat-card {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .stat-card .stat-value {
    font-size: 18px;
  }

  .data-table {
    font-size: 9px;
  }

  .data-table th,
  .data-table td {
    padding: 4px;
  }
}
</style>
